/**
 * StatsPanel.jsx
 * ==============
 * Summary statistics from parcels.json:
 *  - Pie chart: crop type distribution (area)
 *  - Bar chart: growth stage distribution
 *  - KPI cards: total area, parcel count, mean NDVI
 *
 * Uses recharts for charts.
 */

import React, { useMemo } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend,
} from 'recharts';
import parcelsData from '../data/parcels.json';

// ─── Palettes ─────────────────────────────────────────────────────────────────
const CROP_COLORS = {
  Paddy:     '#00FF00',
  Sugarcane: '#FFD700',
  Banana:    '#FF6B35',
  Coconut:   '#228B22',
  Groundnut: '#DEB887',
  Fallow:    '#8B4513',
};
const STAGE_COLORS = {
  Early:        '#FFFF00',
  Intermediate: '#90EE90',
  Mature:       '#006400',
  Harvested:    '#A0522D',
};

// ─── Data Aggregation ─────────────────────────────────────────────────────────
function useStats() {
  return useMemo(() => {
    const features = parcelsData.features;
    let totalArea  = 0;
    const byeCrop  = {};
    const byStage  = {};
    let ndviSum    = 0, ndviCount = 0;

    for (const f of features) {
      const p    = f.properties;
      const area = parseFloat(p.area_hectares) || 0;
      totalArea += area;

      if (!byeCrop[p.crop_type])    byeCrop[p.crop_type]    = 0;
      if (!byStage[p.growth_stage]) byStage[p.growth_stage] = 0;
      byeCrop[p.crop_type]    += area;
      byStage[p.growth_stage] += area;

      if (p.ndvi != null) { ndviSum += p.ndvi; ndviCount++; }
    }

    const cropData = Object.entries(byeCrop).map(([name, area]) => ({
      name, area: +area.toFixed(2),
      pct: +((area / totalArea) * 100).toFixed(1),
      color: CROP_COLORS[name] || '#94a3b8',
    })).sort((a, b) => b.area - a.area);

    const stageData = Object.entries(byStage).map(([name, area]) => ({
      name, area: +area.toFixed(2),
      pct: +((area / totalArea) * 100).toFixed(1),
      color: STAGE_COLORS[name] || '#94a3b8',
    }));

    return {
      totalArea:    +totalArea.toFixed(2),
      parcelCount:  features.length,
      ndviMean:     ndviCount ? +(ndviSum / ndviCount).toFixed(3) : null,
      cropData,
      stageData,
    };
  }, []);
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="glass rounded px-2 py-1 text-xs">
      <div className="font-bold" style={{ color: d.color }}>{d.name}</div>
      <div className="text-slate-300">{d.area} ha ({d.pct}%)</div>
    </div>
  );
};

// ─── Custom Pie Label ─────────────────────────────────────────────────────────
const renderCustomLabel = ({ name, pct, x, y }) => (
  pct > 8 ? (
    <text x={x} y={y} fill="#e2e8f0" textAnchor="middle" fontSize={9} fontWeight={600}>
      {`${name.slice(0,4)} ${pct}%`}
    </text>
  ) : null
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function StatsPanel() {
  const { totalArea, parcelCount, ndviMean, cropData, stageData } = useStats();

  return (
    <div className="space-y-3">

      {/* KPI Cards */}
      <div className="grid grid-cols-3 gap-2">
        <KpiCard icon="🗺️" label="Parcels"    value={parcelCount} />
        <KpiCard icon="📐" label="Total (ha)" value={totalArea}   />
        <KpiCard icon="🌿" label="NDVI Mean"  value={ndviMean ?? '—'} color={
          ndviMean > 0.5 ? '#4ade80' : ndviMean > 0.2 ? '#fbbf24' : '#f87171'
        } />
      </div>

      {/* Crop type pie chart */}
      <div className="glass rounded-lg p-3">
        <div className="text-xs font-bold text-slate-400 mb-2 uppercase tracking-wider">
          🌾 Crop Distribution (ha)
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <PieChart>
            <Pie
              data={cropData}
              dataKey="area"
              nameKey="name"
              cx="50%" cy="50%"
              outerRadius={65}
              labelLine={false}
              label={renderCustomLabel}
            >
              {cropData.map((d, i) => (
                <Cell key={i} fill={d.color} stroke="#0f1923" strokeWidth={1} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        {/* Compact legend */}
        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 mt-1">
          {cropData.map(d => (
            <div key={d.name} className="flex items-center gap-1.5 text-xs">
              <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: d.color }} />
              <span className="text-slate-400 truncate">{d.name}</span>
              <span className="text-slate-500 ml-auto">{d.pct}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Growth stage bar chart */}
      <div className="glass rounded-lg p-3">
        <div className="text-xs font-bold text-slate-400 mb-2 uppercase tracking-wider">
          📈 Growth Stage Distribution
        </div>
        <ResponsiveContainer width="100%" height={120}>
          <BarChart
            data={stageData}
            margin={{ top: 0, right: 4, left: -24, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
            <XAxis
              dataKey="name"
              tick={{ fill: '#94a3b8', fontSize: 9 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fill: '#94a3b8', fontSize: 9 }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="area" radius={[3, 3, 0, 0]}>
              {stageData.map((d, i) => (
                <Cell key={i} fill={d.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Crop area table */}
      <div className="glass rounded-lg p-3">
        <div className="text-xs font-bold text-slate-400 mb-2 uppercase tracking-wider">
          📊 Area by Crop
        </div>
        <div className="space-y-1.5">
          {cropData.map(d => (
            <div key={d.name}>
              <div className="flex justify-between text-xs mb-0.5">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-sm" style={{ background: d.color }} />
                  <span className="text-slate-300">{d.name}</span>
                </div>
                <span className="text-slate-400">{d.area} ha</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${d.pct}%`,
                    background: d.color,
                    opacity: 0.8,
                    transition: 'width 0.5s ease',
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, color }) {
  return (
    <div className="glass rounded-lg p-2 text-center">
      <div className="text-base">{icon}</div>
      <div
        className="text-sm font-bold leading-tight"
        style={{ color: color || '#e2e8f0' }}
      >
        {value}
      </div>
      <div className="text-xs text-slate-500 leading-tight">{label}</div>
    </div>
  );
}
